import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { AUCTION_CATEGORIES } from '@propertypal/shared/src/constants/filterOptions';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { selectMap } from '@propertypal/shared/src/reducers/map';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import getTotalProperties from '@propertypal/shared/src/utils/search/getTotalProperties';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import { closeTooltip, openTooltip } from '@propertypal/web-ui/src/notifications/Tooltip';
import DidYouMean from '@propertypal/web-ui/src/search/DidYouMean';
import { Bold, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import OpenViewingBox from './OpenViewingBox';
import { Container, SideOptions, InfoContent } from './SearchTitle.style.pn';
import UpcomingAuctionsBox from './UpcomingAuctionsBox';

interface Props {
  onAlternativeSearch: (term: string) => void;
  onSaveSearchClick: () => void;
  searchState: UseSearchState;
  onMap?: boolean;
}

const SearchTitle: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const { appFilters, nlpFilters, isSavable, disableSaveSearch } = props.searchState;
  const filters = { ...appFilters, ...nlpFilters };
  const properties = useSelector(selectProperties);
  const mapUrl = `/map${properties.data?.url}`;
  const map = useSelector(selectMap);

  const handleMouseOver = (e: any) => {
    if (disableSaveSearch) {
      openTooltip(
        'You must click the "Search" button in the search form to update your results before you can save this search.',
        e,
      );
    }
  };

  if (props.onMap) {
    const [, formattedTotal] = getTotalProperties(filters.category, map?.totalProperties, map?.totalDevelopments);

    return (
      <div>
        <Container $onMap>
          <div>
            <SubHeading mb={-5}>
              <Bold color={theme.primary}>{formattedTotal}</Bold> homes found
            </SubHeading>
            {map.description && <Text>{map.description.stem}</Text>}
          </div>

          <SideOptions>
            {map.url && (
              <Link href={map.url}>
                <FontAwesomeIcon icon={faList} />
                <span>List View</span>
              </Link>
            )}

            {isSavable && (
              <button
                onMouseEnter={handleMouseOver}
                onMouseLeave={closeTooltip}
                onClick={props.onSaveSearchClick}
                type="button"
              >
                <FontAwesomeIcon icon={faBell} />
                <span>{properties.data?.savedSearch ? 'Edit' : 'Create'} alert</span>
              </button>
            )}
          </SideOptions>
        </Container>
      </div>
    );
  }

  const [, formattedTotal] = getTotalProperties(
    filters.category,
    properties.data?.totalUnits,
    properties.data?.totalDevelopments,
  );

  return (
    <div>
      <Container>
        <div>
          <SubHeading mb={-5}>
            <Bold color={theme.primary}>{formattedTotal}</Bold> homes found
          </SubHeading>
          {properties.data && <Text>{properties.data.description.stem}</Text>}
        </div>

        <SideOptions>
          <Link href={mapUrl}>
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <span>Map View</span>
          </Link>

          {isSavable && (
            <button
              onMouseEnter={handleMouseOver}
              onMouseLeave={closeTooltip}
              onClick={props.onSaveSearchClick}
              type="button"
            >
              <FontAwesomeIcon icon={faBell} />
              <span>{properties.data?.savedSearch ? 'Edit' : 'Create'} alert</span>
            </button>
          )}
        </SideOptions>
      </Container>

      <InfoContent>
        {properties.data &&
          properties.data.results.length > 0 &&
          properties.data?.alternativeSearches &&
          Object.keys(properties.data?.alternativeSearches).length > 0 && (
            <DidYouMean searchResult={properties.data} onClick={props.onAlternativeSearch} />
          )}

        {properties.data?.page === 0 && <OpenViewingBox searchResult={properties.data} />}

        {AUCTION_CATEGORIES.includes(filters.category) && <UpcomingAuctionsBox />}
      </InfoContent>
    </div>
  );
};

export default SearchTitle;
